import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../utils/api';
import { PageHeader, SearchBar, DataTable, Badge, Pagination, Loading, usePersistedState, BackButton } from '../components/Shared';
import { useAuth } from '../context/AuthContext';

const TYPES_DEFAUT   = ['Entreprise', 'Particulier', 'Association', 'Collectivité', 'Autre'];
const STATUTS_DEFAUT = ['Prospect', 'Client actif', 'Client inactif', 'Partenaire'];

function ModalClient({ initial, clientOptions = {}, onSave, onClose }) {
  const TYPES   = clientOptions.types   || TYPES_DEFAUT;
  const STATUTS = clientOptions.statuts || STATUTS_DEFAUT;
  const [form, setForm] = useState(initial || {
    nom_client: '', type_client: TYPES[0] || 'Entreprise', statut_client: STATUTS[0] || 'Prospect',
    adresse1: '', adresse2: '', code_postal: '', ville: '',
    tel_fixe: '', tel_portable: '', site_internet: '', siret: '',
    contact_nom: '', contact_prenom: '', contact_email: '', contact_profession: '',
    representant: '', notes: '', actif: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEdit = !!initial?.client_id;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inputStyle = {
    width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px',
    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box',
  };

  function field(label, key, type = 'text', required = false) {
    return (
      <div style={{ marginBottom: '0.7rem' }}>
        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
          {label}{required && <span style={{ color: '#ef4444' }}> *</span>}
        </label>
        <input type={type} value={form[key] || ''} onChange={e => set(key, e.target.value)} style={inputStyle} />
      </div>
    );
  }

  async function handleSave() {
    if (!form.nom_client) return setError('Le nom du client est requis');
    setSaving(true); setError('');
    try {
      if (isEdit) {
        await apiFetch(`/clients/${initial.client_id}`, { method: 'PUT', body: JSON.stringify(form) });
      } else {
        await apiFetch('/clients', { method: 'POST', body: JSON.stringify(form) });
      }
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '600px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)' }}>
          {isEdit ? `Modifier — ${initial.nom_client}` : '+ Nouveau client'}
        </h3>

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: '0.7rem', letterSpacing: '0.06em' }}>Informations générales</p>
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          {field('Nom du client', 'nom_client', 'text', true)}
        </div>
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          <div style={{ marginBottom: '0.7rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Type</label>
            <select value={form.type_client || ''} onChange={e => set('type_client', e.target.value)} style={inputStyle}>
              {TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: '0.7rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Statut</label>
            <select value={form.statut_client || ''} onChange={e => set('statut_client', e.target.value)} style={inputStyle}>
              {STATUTS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        {field('SIRET', 'siret')}
        {field('Représentant légal', 'representant')}

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Adresse</p>
        {field('Adresse ligne 1', 'adresse1')}
        {field('Adresse ligne 2', 'adresse2')}
        <div className="grid-label" style={{ gap: '0 0.7rem' }}>
          {field('Code postal', 'code_postal')}
          {field('Ville', 'ville')}
        </div>

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Contact principal</p>
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          {field('Prénom', 'contact_prenom')}
          {field('Nom', 'contact_nom')}
          {field('Email', 'contact_email', 'email')}
          {field('Téléphone', 'contact_tel_portable')}
          {field('Tél. fixe', 'tel_fixe')}
          {field('Portable société', 'tel_portable')}
        </div>
        {field('Fonction / Profession', 'contact_profession')}

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Contact secondaire</p>
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          {field('Prénom', 'contact2_prenom')}
          {field('Nom', 'contact2_nom')}
          {field('Email', 'contact2_email', 'email')}
          {field('Tél. portable', 'contact2_tel_portable')}
          {field('Tél. fixe', 'contact2_tel_fixe')}
        </div>
        {field('Fonction / Profession', 'contact2_profession')}

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Autres</p>
        {field('Site internet', 'site_internet', 'url')}
        <div style={{ marginBottom: '0.7rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Notes</label>
          <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={3}
            style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
        {isEdit && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem', marginBottom: '0.7rem' }}>
            <input type="checkbox" checked={form.actif ?? true} onChange={e => set('actif', e.target.checked)} />
            Client actif
          </label>
        )}

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', borderRadius: '6px', padding: '0.5rem 0.8rem', color: '#f87171', fontSize: '0.85rem', marginBottom: '0.8rem' }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : isEdit ? '✓ Enregistrer' : '✓ Créer le client'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Clients() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [search, setSearch] = usePersistedState('clients_search', '');
  const [page, setPage] = usePersistedState('clients_page', 1);
  const [archives, setArchives] = usePersistedState('clients_archives', false);
  const [modal, setModal] = useState(false);
  const [refresh, setRefresh] = useState(0);

  const { data, loading } = useApi('/clients', { search, page, limit: 25, archives: archives ? 'true' : undefined }, [search, page, archives, refresh]);
  const { data: clientOptions } = useApi('/client-options', {}, []);

  const columns = [
    { header: 'ID', key: 'client_id', width: '80px', mobileHide: true },
    { header: 'Client', render: r => <strong>{r.nom_client}</strong> },
    { header: 'Ville', key: 'ville', mobileHide: true },
    { header: 'Contact', render: r => r.contact_nom ? `${r.contact_prenom || ''} ${r.contact_nom}`.trim() : '—' },
    { header: 'Email', render: r => r.contact_email || '—', mobileHide: true },
    { header: 'Type', render: r => <Badge>{r.type_client}</Badge> },
    { header: 'Statut', render: r => <Badge>{r.statut_client}</Badge> },
  ];

  function handleSaved() {
    setModal(false);
    setRefresh(r => r + 1);
  }

  return (
    <div className="page">
      <PageHeader
        title="Clients"
        subtitle={data ? `${data.total} clients` : ''}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <BackButton />
            {hasRole('utilisateur') && <button className="btn-primary" onClick={() => setModal(true)}>+ Nouveau client</button>}
          </div>
        }
      />

      <div className="toolbar">
        <SearchBar value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Rechercher un client..." />
        <div className="toolbar-filters">
          <button className={`filter-chip ${archives ? 'active' : ''}`}
            onClick={() => { setArchives(a => !a); setPage(1); }}
            style={archives ? { borderColor: '#f87171', background: 'rgba(239,68,68,0.15)', color: '#f87171' } : {}}>
            🗄 Archivés
          </button>
        </div>
      </div>

      {loading ? <Loading /> : (
        <>
          <DataTable columns={columns} data={data?.data} onRowClick={r => navigate(`/clients/${r.client_id}`)} />
          <Pagination page={page} total={data?.total || 0} limit={25} onChange={setPage} />
        </>
      )}

      {modal && (
        <ModalClient
          clientOptions={clientOptions || {}}
          onSave={() => { handleSaved(); }}
          onClose={() => setModal(false)}
        />
      )}
    </div>
  );
}
